import { Grid, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import RangeSelectorDatetime from '../common/RangeSelectorDatetime';
import Chart from 'react-apexcharts';
import { useGetFilteredTasksMutation } from '../../services/analytics.service';
import moment, { Moment } from 'moment';

const chartStyle = {
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  bgcolor: '#ffffff',
  border: '1px solid #E2E8F0',
  borderRadius: '8px',
  p: 2,
};

const STATUS_COLORS: Record<string, string> = {
  'To Do': '#EAB308',
  'In Progress': '#0EA5E9',
  'Completed': '#059669',
};

function TasksChart() {
  const [pipeline, setPipeline] = useState({
    ranges: [{ field: 'createdAt', start: moment().subtract(1, 'weeks'), end: moment() }],
    dynamicFilters: [],
    groupOptions: {},
  });
  const [chartData, setChartData] = useState<any>({ categories: [], series: [] });
  const [totalCount, setTotalCount] = useState(0);
  const [getTasks] = useGetFilteredTasksMutation();

  const fetchTasks = async (p: any) => {
    const res = await getTasks(p) as any;
    const items: any[] = res?.data?.[0]?.item ?? [];
    const count: number = res?.data?.[0]?.count ?? 0;

    const byStatus: Record<string, Record<string, number>> = {
      'To Do': {},
      'In Progress': {},
      'Completed': {},
    };

    items.forEach((task: any) => {
      const day = task.createdAt?.split('T')[0];
      if (!day) return;
      const status = task.status ?? 'To Do';
      if (byStatus[status]) {
        byStatus[status][day] = (byStatus[status][day] ?? 0) + 1;
      }
    });

    const allDays = Array.from(
      new Set(items.map((t: any) => t.createdAt?.split('T')[0]).filter(Boolean))
    ).sort((a, b) => new Date(a).valueOf() - new Date(b).valueOf());

    setTotalCount(count);
    setChartData({
      categories: allDays,
      series: Object.entries(byStatus)
        .filter(([, dayMap]) => Object.keys(dayMap).length > 0)
        .map(([status, dayMap]) => ({
          name: status,
          data: allDays.map((day) => dayMap[day] ?? 0),
        })),
    });
  };

  useEffect(() => {
    fetchTasks(pipeline);
  }, [pipeline]);

  const handleRangeChange = (range: { start: Moment | null; end: Moment | null }) => {
    setPipeline({
      ...pipeline,
      ranges: [{ field: 'createdAt', start: range.start?.toISOString() as any, end: range.end?.toISOString() as any }],
    });
  };

  return (
    <Grid item container flex={3} sx={chartStyle}>
      <Grid item container gap={1} sx={{ flexShrink: 0, mb: 1 }} alignItems="center">
        <RangeSelectorDatetime onRangeChange={handleRangeChange} />
        <Paper elevation={0} sx={{ p: 1, border: '1px solid #E2E8F0', borderRadius: '6px' }}>
          <Typography variant="body2" fontWeight={500}>Total Tasks: {totalCount}</Typography>
        </Paper>
      </Grid>

      <Grid item sx={{ flex: 1, minHeight: 0 }}>
        <Chart
          options={{
            chart: { toolbar: { show: false }, background: 'transparent', stacked: false },
            colors: chartData.series.map((s: any) => STATUS_COLORS[s.name] ?? '#0F172A'),
            xaxis: {
              categories: chartData.categories,
              tickAmount: 8,
              labels: { rotate: -45, hideOverlappingLabels: true, style: { fontSize: '10px', colors: '#64748B' } },
            },
            yaxis: { min: 0, forceNiceScale: true, labels: { style: { colors: '#64748B', fontSize: '11px' } } },
            plotOptions: { bar: { borderRadius: 3, columnWidth: '60%' } },
            dataLabels: { enabled: false },
            grid: { borderColor: '#E2E8F0' },
            tooltip: { theme: 'light' },
            legend: { position: 'top' },
          }}
          series={chartData.series}
          type="bar"
          height={300}
        />
      </Grid>
    </Grid>
  );
}

export default TasksChart;
